import UAParser from "ua-parser-js";
import { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch, useSelector } from 'react-redux';
import { OnAuth0Logout } from 'functions/auth0Utils';
import { resetGeneratorState } from 'redux/slices/generator';
import { resetTrainerState } from 'redux/slices/trainer';
import { resetTrainingChartState } from "redux/slices/trainingChart";
import { setUserInfo, resetUserState } from 'redux/slices/user';
import { clearWhichWeightCache } from "redux/slices/whichWeightCache";
import { clearPendingEvents } from "redux/slices/pendingEvents";
import { resetNavigationState } from "redux/slices/navigation";
import { resetActiveWorkoutState } from "redux/slices/activeWorkout";
import { resetAdminState } from "redux/slices/admin";
import { InitializeInterceptors, WhichWeightAPI } from "./WhichWeightAPI";

import CoachDashboardRouter from "views/CoachDashboardRouter";

function App() {

    const dispatch = useDispatch();

    const { user, isAuthenticated, getAccessTokenSilently, isLoading, logout } = useAuth0();

    const userId = useSelector(state => state.user.id);
    const userType = useSelector(state => state.user.type);
    const auth0ConnectionValid = useSelector(state => state.user.auth0ConnectionValid);
    const userOnboarded = useSelector(state => state.user.onboarded);
    const membershipActive = useSelector(state => state.user.membershipActive);
    const overrideOrganizationId = useSelector(state => state.admin.overrideOrganizationId);
    
    const parser = new UAParser();
    const deviceInfo = parser.getResult();

    const [viewGroup, setViewGroup] = useState("loading-user");

    const LoadUserData = async () => {

        const jsonBody = {
            email: user.email,
            overrideOrganizationId: overrideOrganizationId
        }

        // console.log(jsonBody);

        const result = await WhichWeightAPI("BILLING", "GET", "/coach_info", jsonBody);

        // console.log(result.data.data);

        // Logout if user info failed to retrieve

        if (result.data.data === null || result.data.data === undefined) {
            OnAuth0Logout(logout);
            return;
        }

        // Save userInfo to redux store

        const userInfo = {
            id: parseInt(result.data.data.id),
            auth0UserId: result.data.data.auth0UserId,
            firstName: result.data.data.firstName,
            lastName: result.data.data.lastName,
            type: result.data.data.type,
            email: user.email,
            picture: user.picture,
            auth0ConnectionValid: result.data.data.auth0ConnectionValid,
            onboarded: result.data.data.onboarded,
            organizationId: result.data.data.organizationId,
            organizationName: result.data.data.organizationName,
            organizationCode: result.data.data.organizationCode,
            organizationLogo: result.data.data.organizationLogo,
            organizationWebsite: result.data.data.organizationWebsite || '',
            organizationHandlesMembership: result.data.data.organizationHandlesMembership,
            requirePaymentMethodToInviteAthletes: result.data.data.requirePaymentMethodToInviteAthletes,
            organizationAdminStripeCustomerId: result.data.data.organizationAdminStripeCustomerId,
            doesOrganizationHaveAtleastOnePayingAthlete: result.data.data.doesOrganizationHaveAtleastOnePayingAthlete,
            numberOfAssessmentInvitesSent: result.data.data.numberOfAssessmentInvitesSent,
            freeAssessmentInviteExpirationTimestamp: result.data.data.freeAssessmentInviteExpirationTimestamp,
            enableCoachRunningWorkouts: result.data.data.enableCoachRunningWorkouts,
            enableKinesisUserGroups: result.data.data.enableKinesisUserGroups,
            requireEmailToCreateAthleteAccount: result.data.data.requireEmailToCreateAthleteAccount,
            isTrainingPeaksIntegrationActive: result.data.data.isTrainingPeaksIntegrationActive || false
        }

        dispatch(setUserInfo(userInfo));

        // Track login

        const loginInfoJson = {
            userId: result.data.data.id,
            userType: result.data.data.type,
            browser: deviceInfo.browser.name + ' ' + deviceInfo.browser.version,
            device: deviceInfo.device.model,
            operatingSystem: deviceInfo.os.name + ' ' + deviceInfo.os.version
        }

        // console.log(loginInfoJson);

        WhichWeightAPI("CORE", "POST", "/track_login", loginInfoJson);
    };

    // Clear redux cache if user is unauthenticated
    useEffect(() => {

        if (!isAuthenticated && !isLoading) {

            dispatch(resetAdminState());
            dispatch(resetGeneratorState());
            dispatch(resetTrainerState());
            dispatch(resetTrainingChartState());
            dispatch(resetUserState());
            dispatch(clearWhichWeightCache());
            dispatch(clearPendingEvents());
            dispatch(resetNavigationState());
            dispatch(resetActiveWorkoutState());

        }

    }, [isAuthenticated, isLoading]);

    // Load user data once when user is authenticated or user state is cleared
    useEffect(() => {
        if (isAuthenticated && user?.email && userId === null) {
            // console.log('LoadUserData Hook Firing')
            LoadUserData();
        }
    }, [isAuthenticated, user?.email, userId]);

    // Initialize interceptors to use access token in API calls
    useEffect(() => {
        InitializeInterceptors(getAccessTokenSilently, logout);
    }, [getAccessTokenSilently, logout]);

    // Clean up of cache
    useEffect(() => {
        if (localStorage.getItem("BEARER")) localStorage.removeItem("BEARER");
        if (localStorage.getItem("bearerToken")) localStorage.removeItem("bearerToken");
        if (localStorage.getItem("WHICH_WEIGHT_AUTH_0_INTEGRATION_VERSION")) localStorage.removeItem("WHICH_WEIGHT_AUTH_0_INTEGRATION_VERSION");
    }, []);

    // Set view group based on authentication and user state
    useEffect(() => {

        // If auth0 authentication is not loading and isAuthenticated is false, show the unauthenticated app views
        if (
            isLoading === false &&
            isAuthenticated === false
        ) {
            setViewGroup("unauthenticated-user");
            return;
        }

        // If app initialization is not complete, show the loading app views
        if (
            (!isAuthenticated && isLoading) ||
            userId === null ||
            auth0ConnectionValid === null ||
            userType === null ||
            membershipActive === null
        ) {
            setViewGroup("loading-user");
            return;
        }

        // If the auth0 connection is not valid, show the incorrect auth0 connection views
        if (
            auth0ConnectionValid === false
        ) {
            setViewGroup("incorrect-auth0-connection");
            return;
        }

        // If the account is a trainer account, show the incorrect account type views
        if (
            userType === "trainee"
        ) {
            setViewGroup("incorrect-account-type");
            return;
        }

        // If the account is not onboarded, show the onboarding views
        if (
            userOnboarded === false
        ) {
            setViewGroup("authenticated-user-onboarding");
            return;
        }

        // If the account is a trainee account, show the authenticated app views
        if (
            userType === "trainer" || userType === "organization-admin" || userType === "organization-owner" || userType === "super-admin"
        ) {
            // Allow users to see home page even when not onboarded
            // They will see a "Getting Started" section instead
            setViewGroup("authenticated-user");
            return;
        }

        setViewGroup("authenticated-user-error");

    }, [
        isAuthenticated,
        isLoading,
        userId,
        auth0ConnectionValid,
        userType,
        membershipActive
    ]);

    return <CoachDashboardRouter viewGroup={viewGroup} />
}

export default App;